import { getLanguageCode } from 'locales';
import { parseNumberByLocaleWithGroup } from 'locales/utils';
import intl from 'react-intl-universal';
import { DoiInventoryPolicyProcessor } from './doi-inventory-policy.processor';
import {
  type UploadDoiInventoryPolicy,
  UploadDoiInventoryPolicyEnCSVEmpty,
  UploadDoiInventoryPolicyEsCSVEmpty,
  UploadDoiInventoryPolicyPtBrCSVEmpty,
  UploadDoiInventoryPolicyRequiredFieldsEn,
  UploadDoiInventoryPolicyRequiredFieldsEs,
  UploadDoiInventoryPolicyRequiredFieldsPtBr,
} from "./doi-inventory-policy.types";

export const createDoiInventoryPolicyPromises = async (
  fileData: UploadDoiInventoryPolicy[],
  treatSuccess: (data: any) => void,
  treatError: () => void,
  setProcessingPercentual: React.Dispatch<React.SetStateAction<number>>
) => {
  try {
    const processor = new DoiInventoryPolicyProcessor(setProcessingPercentual);
    const summary = await processor.processItems(fileData);
    const response = processor.buildResponse(summary);
    treatSuccess(response);
  } catch (e) {
    console.error('Error processing DOI inventory policy:', e);
    treatError();
  }
};

export const validateDoiInventoryPolicyCsvEmpty = () => {
  const languageCode = getLanguageCode();

  if (languageCode === "pt") {
    return UploadDoiInventoryPolicyPtBrCSVEmpty;
  }

  if (languageCode === "es") {
    return UploadDoiInventoryPolicyEsCSVEmpty;
  }

  return UploadDoiInventoryPolicyEnCSVEmpty;
};

export const validateDoiInventoryPolicyRequiredFields = () => {
  const languageCode = getLanguageCode();

  if (languageCode === "pt") {
    return UploadDoiInventoryPolicyRequiredFieldsPtBr;
  }

  if (languageCode === "es") {
    return UploadDoiInventoryPolicyRequiredFieldsEs;
  }

  return UploadDoiInventoryPolicyRequiredFieldsEn;
};

export const validateDoiInventoryPolicyDelimiter = () => {
  const languageCode = getLanguageCode();

  if (languageCode === "en") {
    return ",";
  }

  return ";";
};

export const formatAndValidateDoiInventoryPolicyNumber = (strNumber: string, validations: string[], field: string) => {
  try {
    if (!strNumber || strNumber.trim() === '') {
      return null;
    }

    const normalizedNumber = parseNumberByLocaleWithGroup(strNumber);

    if (normalizedNumber === null || normalizedNumber === undefined)
      throw new Error();

    const _number = Number(normalizedNumber);

    if (isNaN(_number) || _number < 0)
      throw new Error();

    return _number;

  } catch (e) {
    validations.push(field);
    console.error(e);
    return null;
  }
};

interface IValidationsDoiInventoryPolicyResult {
  isValid: boolean,
  validationMessage: string[]
  data: UploadDoiInventoryPolicy[],
}

const getMissingFields = (data: string[]) => (`${data.join(', ')}.`);

export const getDoiFieldNamesByLanguage = () => {
  const languageCode = getLanguageCode();

  let plantField = 'Unit';
  let targetField = 'Target DOI';
  let minField = 'Min DOI';
  let maxField = 'Max DOI';
  let floorField = 'DOI Floor';

  if (languageCode === 'pt') {
    plantField = 'Centro';
    targetField = 'DOI Alvo';
    minField = 'DOI Mínimo';
    maxField = 'DOI Máximo';
    floorField = 'Piso de DOI';
  } else if (languageCode === 'es') {
    plantField = 'Centro';
    targetField = 'DOI Objetivo';
    minField = 'DOI Mínimo';
    maxField = 'DOI Máximo';
    floorField = 'Piso de DOI';
  }

  return { plantField, targetField, minField, maxField, floorField };
};

export const validateItemRequiredFields = (item: UploadDoiInventoryPolicy, line: number, result: IValidationsDoiInventoryPolicyResult): boolean => {
  const requiredFields = validateDoiInventoryPolicyRequiredFields();
  const dataMissing = requiredFields?.filter((field) => {
    const value = item[field];
    return value === undefined || value === null || value.toString().trim() === '';
  });

  if (dataMissing.length !== 0) {
    result.isValid = false;
    result.validationMessage.push(`${intl.get('master-data.general-configuration.missingRequiredFields')} ${line}: ${getMissingFields(dataMissing)}`);
    return false;
  }

  return true;
};

const parseDoiNumericValue = (raw: any): number | null => {
  if (raw === undefined || raw === null || raw.toString().trim() === '') return null;
  const normalized = parseNumberByLocaleWithGroup(raw.toString());
  if (normalized === null || normalized === undefined) return null;
  const value = Number(normalized);
  return Number.isNaN(value) ? null : value;
};

export const validateItemDoiRange = (item: UploadDoiInventoryPolicy, line: number, result: IValidationsDoiInventoryPolicyResult): boolean => {
  const { targetField, minField, maxField } = getDoiFieldNamesByLanguage();

  const minValue = parseDoiNumericValue(item[minField]);
  const targetValue = parseDoiNumericValue(item[targetField]);
  const maxValue = parseDoiNumericValue(item[maxField]);

  if (minValue === null || targetValue === null || maxValue === null) {
    return true;
  }

  if (minValue > targetValue || targetValue > maxValue) {
    result.isValid = false;
    result.validationMessage.push(`${intl.get('commons.line')} ${line} - ${intl.get('master-data.general-configuration.doiRangeInvalid')}`);
    return false;
  }

  return true;
};

export const validateItemNumericFields = (item: UploadDoiInventoryPolicy, line: number, result: IValidationsDoiInventoryPolicyResult): boolean => {
  const lineValidations: string[] = [];
  const { targetField, minField, maxField, floorField } = getDoiFieldNamesByLanguage();

  if (item[targetField] !== undefined && item[targetField] !== null && item[targetField].toString().trim() !== '') {
    formatAndValidateDoiInventoryPolicyNumber(item[targetField].toString(), lineValidations, targetField);
  }
  if (item[minField] !== undefined && item[minField] !== null && item[minField].toString().trim() !== '') {
    formatAndValidateDoiInventoryPolicyNumber(item[minField].toString(), lineValidations, minField);
  }
  if (item[maxField] !== undefined && item[maxField] !== null && item[maxField].toString().trim() !== '') {
    formatAndValidateDoiInventoryPolicyNumber(item[maxField].toString(), lineValidations, maxField);
  }
  if (item[floorField] !== undefined && item[floorField] !== null && item[floorField].toString().trim() !== '') {
    formatAndValidateDoiInventoryPolicyNumber(item[floorField].toString(), lineValidations, floorField);
  }

  if (lineValidations.length) {
    result.isValid = false;
    result.validationMessage.push(`${intl.get('commons.line')} ${line} - ${intl.get('master-data.general-configuration.fieldErrors')} ${lineValidations.join(', ')}`);
    return false;
  }

  return true;
};

export const processValidationItem = (item: UploadDoiInventoryPolicy, line: number, result: IValidationsDoiInventoryPolicyResult): void => {
  try {
    const isRequiredFieldsValid = validateItemRequiredFields(item, line, result);
    if (!isRequiredFieldsValid) {
      return;
    }

    const isNumericFieldsValid = validateItemNumericFields(item, line, result);
    if (!isNumericFieldsValid) {
      return;
    }

    const isDoiRangeValid = validateItemDoiRange(item, line, result);
    if (!isDoiRangeValid) {
      return;
    }

    result.data.push(item);
  } catch (e) {
    result.isValid = false;
    result.validationMessage.push(`${intl.get('master-data.general-configuration.errorLine')} ${line}`);
    console.error(e);
  }
};

export const filterEmptyItems = (items: UploadDoiInventoryPolicy[]): UploadDoiInventoryPolicy[] => {
  const _items = items[0] as unknown as UploadDoiInventoryPolicy[];
  return _items?.filter(x => x && Object.values(x).some(value => value && value.toString().trim() !== '')) || [];
};

export const validateFileHeaders = (items: UploadDoiInventoryPolicy[]): boolean => {
  const expectedHeaders = validateDoiInventoryPolicyCsvEmpty();
  return Object.keys(expectedHeaders).every(x => Object.keys(items[0]).includes(x));
};

const validateFileStructure = (items: UploadDoiInventoryPolicy[]): { isValid: boolean; filteredItems: UploadDoiInventoryPolicy[]; errorMessage?: string } => {
  const filteredItems = filterEmptyItems(items);

  if (!filteredItems.length) {
    return {
      isValid: false,
      filteredItems: [],
      errorMessage: intl.get('master-data.general-configuration.invalidFile')
    };
  }

  if (!validateFileHeaders(filteredItems)) {
    return {
      isValid: false,
      filteredItems: [],
      errorMessage: intl.get('master-data.general-configuration.incorrectHeader')
    };
  }

  return {
    isValid: true,
    filteredItems
  };
};

const processFileItems = (items: UploadDoiInventoryPolicy[], result: IValidationsDoiInventoryPolicyResult): void => {
  let line = 2;
  for (const item of items) {
    processValidationItem(item, line, result);
    line++;
  }
};

export const formatAndValidateDoiInventoryPolicyFile: (items: UploadDoiInventoryPolicy[]) => IValidationsDoiInventoryPolicyResult = (items: UploadDoiInventoryPolicy[]) => {
  const _result: IValidationsDoiInventoryPolicyResult = {
    data: [],
    isValid: true,
    validationMessage: []
  };

  const fileValidation = validateFileStructure(items);
  if (!fileValidation.isValid) {
    _result.isValid = false;
    _result.validationMessage.push(fileValidation.errorMessage!);
    return _result;
  }

  processFileItems(fileValidation.filteredItems, _result);
  return _result;
};
